import React, { useState, useEffect, useRef } from "react";

const UnifiedMatchResultInput = ({
  match,
  player1,
  player2,
  onSave,
  onCancel,
  onClose,
  players = [],
  mode = "overlay", // "overlay", "inline", "modal"
  title = "Ergebnis eintragen",
  isDirectEntry = false,
  groupId = null,
  allowVariableSets = false, // Für K.O.-Phase: variable Anzahl von Sätzen
  minSets = 2,
  maxSets = 5,
  requireWinner = false, // Für K.O.-Phase: eindeutiger Gewinner erforderlich
}) => {
  const getParticipantName = (participant) => {
    if (!participant) return null;
    if (participant.displayName) return participant.displayName;
    if (participant.name) return participant.name;
    if (Array.isArray(participant.players) && participant.players.length > 0) {
      return participant.players.map((entry) => entry.name).join(" / ");
    }
    return participant.id ? `Team ${participant.id}` : null;
  };

  // Team-Daten bestimmen
  const getPlayer1 = () => {
    if (player1) return player1;
    if (match?.team1) return match.team1;
    if (match?.team1Id && players.length > 0) {
      return players.find(p => p.id === match.team1Id) || { id: match.team1Id, name: `Team ${match.team1Id}` };
    }
    return null;
  };

  const getPlayer2 = () => {
    if (player2) return player2;
    if (match?.team2) return match.team2;
    if (match?.team2Id && players.length > 0) {
      return players.find(p => p.id === match.team2Id) || { id: match.team2Id, name: `Team ${match.team2Id}` };
    }
    return null;
  };

  const currentPlayer1 = getPlayer1();
  const currentPlayer2 = getPlayer2();

  // Initial Sets Setup
  const getInitialSets = () => {
    if (allowVariableSets) {
      // Für K.O.-Phase: mindestens minSets, maximal maxSets
      const existingSets = match?.sets || [];
      const setsArray = [];

      for (let i = 0; i < Math.max(minSets, existingSets.length || minSets); i++) {
        const existingSet = existingSets.find(s => s.setNumber === i + 1);
        setsArray.push([
          existingSet?.player1Score ?? "",
          existingSet?.player2Score ?? ""
        ]);
      }
      return setsArray;
    } else {
      // Für Gruppenphase: immer 3 Sätze
      return [1, 2, 3].map((nr) => {
        const set = match?.sets?.find((s) => s.setNumber === nr);
        return [set?.player1Score ?? "", set?.player2Score ?? ""];
      });
    }
  };

  const [sets, setSets] = useState(getInitialSets());
  const [dirty, setDirty] = useState(false);
  const [date, setDate] = useState(match?.date || "");
  const [court, setCourt] = useState(match?.court || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs auf alle Satz-Eingabefelder, um beim Eintippen automatisch ins
  // nächste Feld zu springen (Auto-Advance).
  const inputRefs = useRef([]);

  const setInputRef = (setIdx, fieldIdx) => (el) => {
    if (!inputRefs.current[setIdx]) inputRefs.current[setIdx] = [];
    inputRefs.current[setIdx][fieldIdx] = el;
  };

  // Springt nach Eingabe in den ersten beiden Sätzen automatisch ins nächste
  // Eingabefeld. Ab dem dritten Satz nicht, da dort (Match-Tiebreak)
  // zweistellige Werte vorkommen können.
  const focusNextField = (setIdx, fieldIdx) => {
    if (setIdx > 1) return;
    let nextSet = setIdx;
    let nextField = fieldIdx + 1;
    if (nextField > 1) {
      nextField = 0;
      nextSet = setIdx + 1;
    }
    const nextInput = inputRefs.current?.[nextSet]?.[nextField];
    if (nextInput) {
      nextInput.focus();
      if (typeof nextInput.select === "function") nextInput.select();
    }
  };

  // Update sets when match changes
  useEffect(() => {
    setSets(getInitialSets());
    setDate(match?.date || "");
    setCourt(match?.court || "");
    setDirty(false);
  }, [match?.sets, match?.id, match?.date, match?.court]);

  const handleSetChange = (setIdx, fieldIdx, value) => {
    const newSets = sets.map((arr, idx) =>
      idx === setIdx
        ? [fieldIdx === 0 ? value : arr[0], fieldIdx === 1 ? value : arr[1]]
        : arr
    );
    setSets(newSets);
    setDirty(true);

    // Nach Eingabe einer Ziffer automatisch ins nächste Feld springen.
    if (value !== "") {
      focusNextField(setIdx, fieldIdx);
    }
  };

  const addSet = () => {
    if (allowVariableSets && sets.length < maxSets) {
      setSets([...sets, ["", ""]]);
    }
  };

  const removeSet = (index) => {
    if (allowVariableSets && sets.length > minSets) {
      const newSets = sets.filter((_, i) => i !== index);
      setSets(newSets);
    }
  };

  const validateSets = () => {
    const completeSets = sets.filter(([p1, p2]) => p1 !== "" && p2 !== "");

    if (completeSets.length === 0) {
      alert("Bitte tragen Sie mindestens ein vollständiges Satzergebnis ein.");
      return false;
    }

    if (allowVariableSets && completeSets.length < minSets) {
      alert(`Mindestens ${minSets} Sätze müssen ausgefüllt werden!`);
      return false;
    }

    if (requireWinner) {
      let player1SetsWon = 0;
      let player2SetsWon = 0;

      completeSets.forEach(([p1, p2]) => {
        const score1 = Number(p1);
        const score2 = Number(p2);
        if (score1 > score2) {
          player1SetsWon++;
        } else if (score2 > score1) {
          player2SetsWon++;
        }
      });

      if (player1SetsWon === player2SetsWon) {
        alert('Es muss einen eindeutigen Gewinner geben! Unentschieden ist in der K.O.-Phase nicht erlaubt.');
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateSets()) return;

    setIsSubmitting(true);

    try {
      const setResults = sets
        .map(([p1, p2], idx) =>
          p1 !== "" && p2 !== ""
            ? {
                setNumber: idx + 1,
                player1Score: Number(p1),
                player2Score: Number(p2),
              }
            : null
        )
        .filter(Boolean);

      if (isDirectEntry) {
        // Für direkte Ergebniseingabe
        await onSave({
          team1Id: currentPlayer1.id,
          team2Id: currentPlayer2.id,
          groupId: groupId,
          date: date || null,
          court: court || null,
          setResults: setResults
        });
      } else {
        // Für normale Ergebniseingabe
        await onSave(match.id, setResults, date, court);
      }

      setDirty(false);
      if (onClose) onClose();
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      alert("Fehler beim Speichern des Ergebnisses: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };

  const renderContent = () => (
    <>
      <h2>{title}</h2>

      {currentPlayer1 && currentPlayer2 && (
        <div style={{ marginBottom: 16 }}>
          <b>{getParticipantName(currentPlayer1)}</b>
          &nbsp;vs.&nbsp;
          <b>{getParticipantName(currentPlayer2)}</b>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        {/* Optionale Felder für Datum und Platz */}
        <div className="mri-meta-row">
          <label className="mri-field">
            Datum (optional):
            <input
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setDirty(true);
              }}
              className="groupphase-input"
              disabled={isSubmitting}
            />
          </label>
          <label className="mri-field">
            Platz (optional):
            <input
              type="text"
              value={court}
              onChange={(e) => {
                setCourt(e.target.value);
                setDirty(true);
              }}
              className="groupphase-input"
              placeholder="z.B. Platz 1"
              disabled={isSubmitting}
            />
          </label>
        </div>

        {/* Sätze eingeben */}
        <div>
          <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
            Satzergebnisse:
          </label>

          {allowVariableSets ? (
            // Variable Sätze für K.O.-Phase
            <div>
              <div className="sets-header" style={{ display: "flex", gap: 12, marginBottom: 8, fontWeight: "bold" }}>
                <span style={{ width: 60 }}>Satz</span>
                <span style={{ width: 100 }}>{getParticipantName(currentPlayer1)}</span>
                <span style={{ width: 100 }}>{getParticipantName(currentPlayer2)}</span>
                <span style={{ width: 80 }}>Aktion</span>
              </div>

              {sets.map((set, index) => (
                <div key={index} style={{ display: "flex", gap: 12, marginBottom: 8, alignItems: "center" }}>
                  <span style={{ width: 60 }}>Satz {index + 1}</span>
                  <input
                    ref={setInputRef(index, 0)}
                    type="number"
                    min="0"
                    value={set[0]}
                    onChange={(e) => handleSetChange(index, 0, e.target.value)}
                    className="groupphase-input"
                    style={{ width: 60, textAlign: "center" }}
                    placeholder="0"
                    disabled={isSubmitting}
                  />
                  <input
                    ref={setInputRef(index, 1)}
                    type="number"
                    min="0"
                    value={set[1]}
                    onChange={(e) => handleSetChange(index, 1, e.target.value)}
                    className="groupphase-input"
                    style={{ width: 60, textAlign: "center" }}
                    placeholder="0"
                    disabled={isSubmitting}
                  />
                  <div style={{ width: 80 }}>
                    {sets.length > minSets && (
                      <button
                        type="button"
                        onClick={() => removeSet(index)}
                        className="groupphase-button"
                        style={{ fontSize: 12, padding: "2px 8px", backgroundColor: "#ff4444", color: "white" }}
                        title="Satz entfernen"
                        disabled={isSubmitting}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {sets.length < maxSets && (
                <button
                  type="button"
                  onClick={addSet}
                  className="groupphase-button"
                  style={{ marginTop: 8 }}
                  disabled={isSubmitting}
                >
                  + Weiteren Satz hinzufügen
                </button>
              )}
            </div>
          ) : (
            // Feste 3 Sätze für Gruppenphase
            <div className="mri-sets-row">
              {sets.map((set, setIdx) => (
                <span key={setIdx} className="mri-set">
                  <label>Satz {setIdx + 1}</label>
                  <input
                    ref={setInputRef(setIdx, 0)}
                    type="number"
                    min="0"
                    value={set[0]}
                    placeholder="0"
                    onChange={(e) => handleSetChange(setIdx, 0, e.target.value)}
                    className="groupphase-input"
                    style={{ width: 38, textAlign: "center" }}
                    disabled={isSubmitting}
                  />
                  <span>:</span>
                  <input
                    ref={setInputRef(setIdx, 1)}
                    type="number"
                    min="0"
                    value={set[1]}
                    placeholder="0"
                    onChange={(e) => handleSetChange(setIdx, 1, e.target.value)}
                    className="groupphase-input"
                    style={{ width: 38, textAlign: "center" }}
                    disabled={isSubmitting}
                  />
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          {(onCancel || onClose) && (
            <button
              type="button"
              onClick={handleCancel}
              className="groupphase-button"
              disabled={isSubmitting}
              style={{ backgroundColor: "#ccc" }}
            >
              Abbrechen
            </button>
          )}
          <button
            type="submit"
            disabled={!dirty || isSubmitting}
            className="groupphase-button"
          >
            {isSubmitting ? "Speichern..." : "Speichern"}
          </button>
        </div>
      </form>
    </>
  );

  // Render je nach Modus
  if (mode === "overlay" || mode === "modal") {
    return (
      <div className="groupphase-overlay">
        <div className="groupphase-overlay-content">
          {onClose && (
            <button
              className="groupphase-overlay-close"
              onClick={onClose}
              title="Schließen"
              disabled={isSubmitting}
            >
              &times;
            </button>
          )}
          {renderContent()}
        </div>
      </div>
    );
  }

  // Inline-Modus
  return (
    <div className="match-result-input">
      {renderContent()}
    </div>
  );
};

export default UnifiedMatchResultInput;
